import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {DialogContent} from '../../../../../../../src/components/common/dialogs/dialogContent';
import {
  ExportCardsJobOptionTypesEnum,
  ExportContactsCsvJobModel,
} from '../../../../../../../src/models/api/jobModel';
import {reportToBugsnag} from '../../../../../../../srcCommon/services/bugsnag/bugsnag';
import {Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {AsyncHandlerOf, Handler} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {Dropdown} from '../../../../../../shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../../shared/ui/src/components/dropdowns/dropdownHeader';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../../shared/ui/src/styles/fontStyles';
import {ExportContactsButton} from './exportContactsButton';
import {ExportContactsDropdown} from './exportContactsDropdown';

/*
 * Constants.
 */

const defaultDropdownWidth = 300; // px.

/*
 * Props.
 */

interface ExportContactsPopoverProps {
  hasContactsManagerExportContacts: boolean;
  exportJobsInProgress: ReadonlyArray<ExportContactsCsvJobModel>;
  onExportCardsAsync: AsyncHandlerOf<ExportCardsJobOptionTypesEnum>;
  defaultExportType?: ExportCardsJobOptionTypesEnum;
}

/*
 * Styles.
 */

const StyledButtonGroupWrapper = styled(ButtonGroup)`
  margin: 0 20px 20px 20px;
  display: flex;
  justify-content: end;
`;

const StyledErrorDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  margin-top: 6px;
`;

/*
 * Component.
 */

export const ExportContactsPopover: FC<ExportContactsPopoverProps> = ({
  hasContactsManagerExportContacts,
  exportJobsInProgress,
  onExportCardsAsync,
  defaultExportType = ExportCardsJobOptionTypesEnum.COMPANY,
}) => {
  const [exportType, setExportType] = useState(defaultExportType);
  if (!hasContactsManagerExportContacts) {
    return null;
  }

  const onExport = () => onExportCardsAsync(exportType);
  const isExportInProgress = computeIsExportInProgress(exportType, exportJobsInProgress);

  return (
    <PopoverCoordinator
      renderButton={() => <ExportContactsButton />}
      renderModal={(popoverProps) => (
        <Dropdown {...popoverProps} width={defaultDropdownWidth} placement="bottom-end" hasBackground>
          <DropdownHeader title={<Trans id="export-contacts-popover-title">Export contacts</Trans>} />
          <DialogContent>
            <ExportContactsDropdown
              selectedExportType={exportType}
              onSetExportType={setExportType}
              hasError={isExportInProgress}
            />
            {isExportInProgress && (
              <StyledErrorDiv>
                <Trans id="export-contacts-already-in-progress">Export already in progress</Trans>
              </StyledErrorDiv>
            )}
          </DialogContent>

          {renderButtonGroup(popoverProps, isExportInProgress, onExport)}
        </Dropdown>
      )}
      onPopoverToggle={() => setExportType(defaultExportType)} // reset selection to default
    />
  );
};

/*
 * Helpers.
 */

function computeIsExportInProgress(
  selectedExportType: ExportCardsJobOptionTypesEnum,
  exportJobsInProgress: ReadonlyArray<ExportContactsCsvJobModel>,
) {
  return Boolean(exportJobsInProgress.find((job) => job.exportType === selectedExportType));
}

function renderButtonGroup(popoverProps: PopoverTransferProps, isDisabled: boolean, onExport: Handler) {
  const onExportAsync = async () => {
    try {
      await onExport();
      popoverProps.onRequestClose();
    } catch (error) {
      // Do not close when we error.
      reportToBugsnag(error);
    }
  };
  return (
    <StyledButtonGroupWrapper>
      <Button isRounded onClick={popoverProps.onRequestClose}>
        <Trans id="export-contacts-popover-cancel-button">Cancel</Trans>
      </Button>
      <Button isRounded isDisabled={isDisabled} variant={ButtonVariantsEnum.PRIMARY} onClick={onExportAsync}>
        <Trans id="export-contacts-popover-generate-export-button">Generate export</Trans>
      </Button>
    </StyledButtonGroupWrapper>
  );
}
